import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom"
import { LoginFormSchema, type LoginForm } from "@/lib/schemas"
import { useAuthStore } from "@/stores/auth-store"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Loader2, AlertCircle } from "lucide-react"
import { cn } from "@/lib/utils"
import { BrandLockup } from "@/components/BrandLockup"
import { Seo } from "@/components/seo/Seo"
import { resolveLoginRedirect } from "@/lib/redirect"

interface FromLocationState {
  from?: { pathname: string; search?: string; hash?: string }
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const inviteToken = searchParams.get("invite")
  // Return the user to whatever they originally asked for. Notification emails
  // deep-link into a bid/section, so losing this dumps a reviewer on Home with
  // no idea what they were asked to look at.
  const fromState = (location.state as FromLocationState | null)?.from ?? null
  const redirectTo = inviteToken
    ? `/invite/${inviteToken}`
    : resolveLoginRedirect({ redirectParam: searchParams.get("redirect"), fromState })
  const { signIn, isLoading, isAuthenticated, error, clearError, initialize } = useAuthStore()

  // Initialize auth state and redirect if already authenticated
  useEffect(() => {
    void initialize()
  }, [initialize])

  useEffect(() => {
    if (isAuthenticated) {
      void navigate(redirectTo, { replace: true })
    }
  }, [isAuthenticated, navigate, redirectTo])

  const form = useForm<LoginForm>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const {
    register,
    formState: { errors, isSubmitting },
  } = form

  const onSubmit = async (data: LoginForm) => {
    clearError()
    try {
      await signIn(data.email, data.password)
      void navigate(redirectTo, { replace: true })
    } catch {
      // Error is handled by the store
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center hero-gradient p-4">
      <Seo
        title="Log in - BidContext"
        description="Log in to your BidContext workspace."
        canonical="/login"
        noindex
      />
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-6">
          <BrandLockup iconSize={24} wordmarkClassName="text-2xl" />
        </div>
        <Card className="p-4">
        <CardHeader className="space-y-3">
          <CardTitle className="text-lg text-center">Welcome back</CardTitle>
          <CardDescription className="text-center">
            Enter your credentials to access your account
          </CardDescription>
        </CardHeader>
        <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
          <CardContent className="space-y-4">
            {/* Error Banner */}
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                {...register("email")}
                className={cn(errors.email && "border-destructive")}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                {...register("password")}
                className={cn(errors.password && "border-destructive")}
              />
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password.message}</p>
              )}
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-xs text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting || isLoading}
            >
              {(isSubmitting || isLoading) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Sign In
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Don't have an account?{" "}
              <Link
                to="/book-a-demo"
                className="text-[hsl(var(--brand))] hover:text-[hsl(var(--brand-hover))] underline-offset-4 hover:underline"
              >
                Book a demo
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
      </div>
    </div>
  )
}
